@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)

package com.example.monetix.ui.dialogs

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.tween
import androidx.compose.ui.draw.blur
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDownward
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.outlined.CameraAlt
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Mic
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.*
import com.example.monetix.ui.components.MonetixTextField as OutlinedTextField
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.zIndex
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.compose.ui.window.PopupProperties
import coil3.compose.AsyncImage

import com.example.monetix.db.SavingsGoalEntity
import com.example.monetix.ui.components.TxLogo
import com.example.monetix.utils.rememberMediaHandler
import kotlinx.datetime.Clock
import com.example.monetix.utils.parseEuroToCents

// ==========================================
// 🚀 IL MOTORE SEMANTICO PER I SOGNI
// ==========================================
private data class PredefGoal(
    val name: String,
    val emoji: String? = null,
    val customUrl: String? = null
)

private val defaultGoalSuggestions = listOf(
    PredefGoal("Fondo Emergenza", emoji = "🛡️"),
    PredefGoal("Macchina", emoji = "🚗"),
    PredefGoal("Casa", emoji = "🏠"),
    PredefGoal("Viaggio", emoji = "✈️"),
    PredefGoal("Matrimonio", emoji = "💍"),
    PredefGoal("PC / Tech", emoji = "💻"),
    PredefGoal("Regali", emoji = "🎁"),
    PredefGoal("Tasse", emoji = "🏛️")
)

private fun detectSmartGoal(input: String): PredefGoal? {
    val cleanInput = input.trim().lowercase()
    if (cleanInput.length < 3) return null
    return defaultGoalSuggestions.firstOrNull { it.name.lowercase().contains(cleanInput) }
}

// ==========================================
// 🚀 COMPONENTI ISOLATI E PRIVATI
// ==========================================

@Composable
private fun StableWebTextField(
    value: TextFieldValue,
    onValueChange: (TextFieldValue) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    trailingIcon: @Composable (() -> Unit)? = null,
    leadingIcon: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    keyboardType: KeyboardType = KeyboardType.Text
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label, style = MaterialTheme.typography.labelSmall) },
        maxLines = 1,
        enabled = enabled,
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        trailingIcon = trailingIcon,
        leadingIcon = leadingIcon,
        interactionSource = interactionSource,
        keyboardOptions = KeyboardOptions(
            keyboardType = keyboardType,
            autoCorrectEnabled = false
        ),
        colors = OutlinedTextFieldDefaults.colors(
            focusedTextColor = Color.White,
            unfocusedTextColor = Color.White,
            focusedContainerColor = Color.Black.copy(alpha = 0.35f),
            unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
            focusedBorderColor = Color.White.copy(alpha = 0.40f),
            unfocusedBorderColor = Color.White.copy(alpha = 0.20f),
            focusedLabelColor = Color.White,
            unfocusedLabelColor = Color(0xFFA0A0A0)
        )
    )
}

// ==========================================
// 🚀 POPUP RICERCA IMMAGINI WEB AUTOMATICA
// ==========================================
@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun WebImagePickerPopup(
    initialQuery: String,
    onDismiss: () -> Unit,
    onImageSelected: (String) -> Unit
) {
    var query by remember { mutableStateOf(initialQuery) }
    var results by remember { mutableStateOf<List<String>>(emptyList()) }
    val uriHandler = LocalUriHandler.current

    val popularBrands = remember {
        listOf("Amazon", "Netflix", "Spotify", "PayPal", "Iliad", "Postepay", "Enel", "Trenitalia", "Conad", "Coop", "McDonald's", "Apple", "Uber", "Deliveroo")
    }

    fun generateLinks(qRaw: String) {
        val q = qRaw.trim().lowercase().replace(" ", "")
        if (q.isEmpty()) {
            results = emptyList()
            return
        }
        val domain = if (q.contains(".")) q else "$q.com"
        val domainIt = if (q.contains(".")) q else "$q.it"

        results = listOf(
            "https://unavatar.io/$domain",
            "https://unavatar.io/$domainIt",
            "https://icon.horse/icon/$domain",
            "https://icon.horse/icon/$domainIt",
            "https://www.google.com/s2/favicons?domain=$domain&sz=128",
            "https://www.google.com/s2/favicons?domain=$domainIt&sz=128"
        ).distinct()
    }

    LaunchedEffect(initialQuery) {
        if (initialQuery.isNotBlank()) generateLinks(initialQuery)
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        shape = RoundedCornerShape(28.dp),
        containerColor = Color(0xFF1E1B4B),
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Surface(
                    shape = CircleShape,
                    color = Color(0xFF60A5FA).copy(alpha = 0.2f),
                    modifier = Modifier.size(36.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.Search,
                        contentDescription = null,
                        tint = Color(0xFF60A5FA),
                        modifier = Modifier.padding(8.dp)
                    )
                }
                Spacer(Modifier.width(12.dp))
                Column {
                    Text(
                        "Ricerca Logo Web 🌐",
                        style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White
                    )
                    Text(
                        "Trova il logo ufficiale del merchant",
                        style = MaterialTheme.typography.labelSmall,
                        color = Color.White.copy(alpha = 0.6f)
                    )
                }
            }
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                OutlinedTextField(
                    value = query,
                    onValueChange = {
                        query = it
                        generateLinks(it)
                    },
                    label = { Text("Brand o Nome Azienda", color = Color(0xFFA0A0A0)) },
                    placeholder = { Text("es. Netflix, Amazon, Iliad...", color = Color(0xFFA0A0A0)) },
                    trailingIcon = {
                        if (query.isNotBlank()) {
                            IconButton(onClick = { query = ""; results = emptyList() }) {
                                Icon(Icons.Default.Close, contentDescription = "Cancella", tint = Color.White.copy(alpha = 0.6f))
                            }
                        } else {
                            Icon(Icons.Default.Search, contentDescription = null, tint = Color(0xFF60A5FA))
                        }
                    },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedTextColor = Color.White,
                        unfocusedTextColor = Color.White,
                        focusedContainerColor = Color.Black.copy(alpha = 0.35f),
                        unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
                        focusedBorderColor = Color(0xFF60A5FA),
                        unfocusedBorderColor = Color.White.copy(alpha = 0.20f)
                    )
                )

                Text("Brand frequenti:", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.7f))
                LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    items(popularBrands) { brand ->
                        FilterChip(
                            selected = query.equals(brand, ignoreCase = true),
                            onClick = { query = brand; generateLinks(brand) },
                            label = { Text(brand, fontSize = 11.sp, fontWeight = FontWeight.SemiBold) },
                            colors = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = Color(0xFF60A5FA),
                                selectedLabelColor = Color.White,
                                containerColor = Color.White.copy(alpha = 0.08f),
                                labelColor = Color.White.copy(alpha = 0.8f)
                            ),
                            border = null
                        )
                    }
                }

                if (results.isNotEmpty()) {
                    Text("Tocca il logo da usare:", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.7f))
                    LazyVerticalGrid(
                        columns = GridCells.Adaptive(64.dp),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp),
                        modifier = Modifier.height(160.dp).fillMaxWidth()
                    ) {
                        items(results) { url ->
                            Surface(
                                modifier = Modifier
                                    .aspectRatio(1f)
                                    .clickable { onImageSelected(url) },
                                shape = RoundedCornerShape(16.dp),
                                color = Color.White.copy(alpha = 0.15f),
                                border = BorderStroke(1.dp, Color.White.copy(alpha = 0.25f))
                            ) {
                                AsyncImage(
                                    model = url,
                                    contentDescription = null,
                                    modifier = Modifier.fillMaxSize().padding(10.dp),
                                    contentScale = ContentScale.Fit
                                )
                            }
                        }
                    }
                } else {
                    Box(modifier = Modifier.height(80.dp).fillMaxWidth(), contentAlignment = Alignment.Center) {
                        Text("Digita il nome di un brand per caricare i loghi", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
                    }
                }

                HorizontalDivider(color = Color.White.copy(alpha = 0.10f))

                Button(
                    onClick = {
                        val q = if (query.isNotBlank()) "${query} logo icon" else "logo icon"
                        uriHandler.openUri("https://www.google.com/search?tbm=isch&q=${q.replace(" ", "+")}")
                    },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.12f), contentColor = Color.White)
                ) {
                    Icon(Icons.Outlined.Search, contentDescription = null, modifier = Modifier.size(18.dp), tint = Color(0xFF60A5FA))
                    Spacer(Modifier.width(8.dp))
                    Text("Cerca immagini su Google ↗", style = MaterialTheme.typography.labelMedium)
                }

                var manualUrl by remember { mutableStateOf("") }
                OutlinedTextField(
                    value = manualUrl,
                    onValueChange = { manualUrl = it },
                    label = { Text("O incolla URL immagine", color = Color(0xFFA0A0A0)) },
                    placeholder = { Text("https://...", color = Color(0xFFA0A0A0)) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedTextColor = Color.White,
                        unfocusedTextColor = Color.White,
                        focusedContainerColor = Color.Black.copy(alpha = 0.35f),
                        unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
                        focusedBorderColor = Color(0xFF60A5FA),
                        unfocusedBorderColor = Color.White.copy(alpha = 0.20f)
                    )
                )

                AnimatedVisibility(visible = manualUrl.isNotBlank()) {
                    Column(
                        modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Surface(
                            modifier = Modifier.size(64.dp),
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White.copy(alpha = 0.15f),
                            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.25f))
                        ) {
                            AsyncImage(
                                model = manualUrl.trim(),
                                contentDescription = "Anteprima URL",
                                modifier = Modifier.fillMaxSize().padding(8.dp),
                                contentScale = ContentScale.Fit
                            )
                        }
                        Spacer(Modifier.height(10.dp))
                        Button(
                            onClick = { onImageSelected(manualUrl.trim()) },
                            shape = RoundedCornerShape(12.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
                        ) {
                            Text("Usa questa immagine", fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        },
        confirmButton = {},
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("ANNULLA", color = Color.White.copy(alpha = 0.6f), fontWeight = FontWeight.Bold)
            }
        }
    )
}

@Composable
fun SavingsGoalDialog(
    initial: SavingsGoalEntity?,
    onDismiss: () -> Unit,
    onDelete: (String) -> Unit,
    onSave: (SavingsGoalEntity) -> Unit
) {
    val brandPrimary = Color(0xFF60A5FA)
    val nowMs = Clock.System.now().toEpochMilliseconds()

    val pacTag = remember(initial) { Regex("\\[PAC:.*?\\]").find(initial?.name ?: "")?.value ?: "" }
    val accTag = remember(initial) { Regex("\\[ACC:.*?\\]").find(initial?.name ?: "")?.value ?: "" }
    val isPac = pacTag.isNotBlank()

    val initialName = initial?.name?.replace(Regex("\\[(PAC|ACC):.*?\\]"), "")?.trim() ?: ""
    var nameTFV by remember { mutableStateOf(TextFieldValue(initialName, TextRange(initialName.length))) }

    val initialTargetStr = initial?.let {
        val e = it.targetAmountCents / 100
        val c = kotlin.math.abs(it.targetAmountCents % 100).toString().padStart(2, '0')
        "$e,$c"
    } ?: ""
    var targetTFV by remember { mutableStateOf(TextFieldValue(initialTargetStr, TextRange(initialTargetStr.length))) }

    val initialSavedStr = initial?.let {
        val e = it.savedAmountCents / 100
        val c = kotlin.math.abs(it.savedAmountCents % 100).toString().padStart(2, '0')
        "$e,$c"
    } ?: ""
    var savedTFV by remember { mutableStateOf(TextFieldValue(initialSavedStr, TextRange(initialSavedStr.length))) }

    var accLogoEmoji by remember { mutableStateOf(initial?.emoji ?: "") }
    var accLogoUri by remember { mutableStateOf(initial?.imageUri ?: "") }
    var showEmojiInput by remember { mutableStateOf(false) }
    var showWebSearch by remember { mutableStateOf(false) }
    var isCompleted by remember { mutableStateOf(initial?.isCompleted ?: false) }
    var showNotEmptyAlert by remember { mutableStateOf(false) }

    var goalSuggestionsExpanded by remember { mutableStateOf(false) }

    LaunchedEffect(nameTFV.text) {
        if (nameTFV.text.trim().length >= 3) {
            val match = detectSmartGoal(nameTFV.text)
            if (match != null) {
                if (accLogoUri.isBlank() && accLogoEmoji.isBlank()) {
                    if (match.customUrl != null) accLogoUri = match.customUrl
                    if (match.emoji != null) accLogoEmoji = match.emoji
                }
            }
        }
    }

    val mediaHandler = rememberMediaHandler(
        onImageResult = { uri -> if (uri != null) { accLogoUri = uri; accLogoEmoji = "" } },
        onVoiceResult = { }
    )

    val canSave = remember(nameTFV.text, targetTFV.text, savedTFV.text) {
        val targetAmt = parseEuroToCents(targetTFV.text)
        val savedAmt = parseEuroToCents(savedTFV.text)
        nameTFV.text.isNotBlank() && targetAmt > 0L && savedAmt >= 0L
    }

    val bgGradient = remember {
        Brush.verticalGradient(
            colors = listOf(
                Color(0xFF0284C7).copy(alpha = 0.75f),
                Color(0xFF0C4A6E),
                Color(0xFF0F172A)
            )
        )
    }

    val dialogBlur by animateDpAsState(
        targetValue = if (showWebSearch) 12.dp else 0.dp,
        animationSpec = tween(durationMillis = 300),
        label = "dialogBlur"
    )

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Surface(
            modifier = Modifier.fillMaxSize(),
            color = Color.Transparent
        ) {
            Box(modifier = Modifier.fillMaxSize().background(bgGradient).blur(dialogBlur)) {
                Box(modifier = Modifier.fillMaxSize().systemBarsPadding().imePadding()) {
                    Column(modifier = Modifier.fillMaxSize()) {

                        // HEADER STILE REVOLUT
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 16.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            IconButton(
                                onClick = onDismiss,
                                modifier = Modifier.size(40.dp).background(Color.White.copy(alpha = 0.12f), CircleShape)
                            ) {
                                Icon(Icons.Default.ArrowBack, contentDescription = "Indietro", tint = Color.White)
                            }
                            if (initial != null) {
                                IconButton(
                                    onClick = {
                                        if (initial.savedAmountCents > 0L) {
                                            showNotEmptyAlert = true
                                        } else {
                                            onDelete(initial.id)
                                        }
                                    },
                                    modifier = Modifier.size(40.dp).background(Color.White.copy(alpha = 0.12f), CircleShape)
                                ) {
                                    Icon(Icons.Outlined.Delete, contentDescription = "Elimina", tint = MaterialTheme.colorScheme.error)
                                }
                            } else {
                                IconButton(onClick = { showWebSearch = true }, modifier = Modifier.size(40.dp).background(Color.White.copy(alpha = 0.12f), CircleShape)) {
                                    Icon(Icons.Outlined.Search, contentDescription = "Cerca Web", tint = Color.White)
                                }
                            }
                        }

                // CORPO SCORREVOLE
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = 24.dp, vertical = 8.dp),
                    verticalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    Text(
                        text = if (initial == null) "Nuovo Sogno" else if (isPac) "Modifica PAC" else "Modifica Sogno",
                        style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White
                    )

                    // 🚀 ANTEPRIMA LIVE INTELLIGENTE (Stile Card "Dettagli")
                    val liveUri = accLogoUri.takeIf { it.isNotBlank() }
                    val liveEmoji = accLogoEmoji.takeIf { it.isNotBlank() }

                    Surface(
                        shape = RoundedCornerShape(20.dp),
                        color = Color.White.copy(alpha = 0.08f),
                        border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Row(
                            modifier = Modifier.padding(16.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(48.dp)
                                    .clip(RoundedCornerShape(12.dp))
                                    .background(Color.White.copy(alpha = 0.10f)),
                                contentAlignment = Alignment.Center
                            ) {
                                when {
                                    !liveUri.isNullOrBlank() -> {
                                        val isWebImage = liveUri.startsWith("http", ignoreCase = true)
                                        AsyncImage(
                                            model = liveUri,
                                            contentDescription = null,
                                            modifier = Modifier.fillMaxSize().padding(if (isWebImage) 8.dp else 0.dp).clip(RoundedCornerShape(12.dp)),
                                            contentScale = if (isWebImage) ContentScale.Fit else ContentScale.Crop
                                        )
                                    }
                                    !liveEmoji.isNullOrBlank() -> Text(liveEmoji, fontSize = 24.sp)
                                    else -> Icon(Icons.Outlined.CameraAlt, null, tint = Color.White.copy(alpha=0.5f), modifier = Modifier.size(24.dp))
                                }
                            }
                            Spacer(Modifier.width(16.dp))
                            Column(modifier = Modifier.weight(1f)) {
                                Text("Logo del Sogno", style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                                Text("Tocca le opzioni sotto per cambiarlo", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                            }
                        }
                    }

                    // Tasti opzioni immagine (Emoji / Foto)
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Surface(onClick = { showEmojiInput = !showEmojiInput }, shape = RoundedCornerShape(16.dp), color = Color.White.copy(alpha = 0.10f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.25f)), modifier = Modifier.weight(1f).height(40.dp)) {
                            Box(contentAlignment = Alignment.Center) { Text("Emoji", color = Color.White, fontWeight = FontWeight.Bold) }
                        }
                        Surface(onClick = { mediaHandler.launchGallery() }, shape = RoundedCornerShape(16.dp), color = Color.White.copy(alpha = 0.10f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.25f)), modifier = Modifier.weight(1f).height(40.dp)) {
                            Box(contentAlignment = Alignment.Center) { Text("Galleria", color = Color.White, fontWeight = FontWeight.Bold) }
                        }
                    }

                    AnimatedVisibility(visible = showEmojiInput) {
                        OutlinedTextField(
                            value = accLogoEmoji,
                            onValueChange = { if (it.length <= 3) { accLogoEmoji = it; accLogoUri = "" } },
                            label = { Text("Inserisci Emoji", color = Color(0xFFA0A0A0)) },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                            shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF0284C7), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                        )
                    }

                    // CAMPO NOME CONTO SMART CON SUGGERIMENTI DINAMICI
                    Box(modifier = Modifier.fillMaxWidth()) {
                        var isNameFocused by remember { mutableStateOf(false) }
                        OutlinedTextField(
                            value = nameTFV,
                            onValueChange = {
                                nameTFV = it
                                goalSuggestionsExpanded = it.text.isNotBlank()
                            },
                            label = { Text("Nome obiettivo (es. Macchina)", color = Color(0xFFA0A0A0)) },
                            modifier = Modifier.fillMaxWidth().onFocusChanged { isNameFocused = it.isFocused },
                            shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF0284C7), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                        )

                        val filteredGoals = defaultGoalSuggestions.filter {
                            it.name.contains(nameTFV.text, ignoreCase = true)
                        }

                        DropdownMenu(
                            expanded = goalSuggestionsExpanded && filteredGoals.isNotEmpty() && isNameFocused,
                            onDismissRequest = { goalSuggestionsExpanded = false },
                            properties = PopupProperties(focusable = false),
                            modifier = Modifier.fillMaxWidth(0.8f).background(Color(0xFF1C1C1E))
                        ) {
                            filteredGoals.take(5).forEach { goal ->
                                DropdownMenuItem(
                                    text = { Text(goal.name, fontWeight = FontWeight.SemiBold, color = Color.White) },
                                    leadingIcon = {
                                        when {
                                            goal.customUrl != null -> AsyncImage(model = goal.customUrl, contentDescription = null, modifier = Modifier.size(24.dp))
                                            goal.emoji != null -> Text(goal.emoji, fontSize = 20.sp)
                                        }
                                    },
                                    onClick = {
                                        nameTFV = TextFieldValue(goal.name, TextRange(goal.name.length))
                                        if (accLogoUri.isBlank() && accLogoEmoji.isBlank()) {
                                            accLogoUri = goal.customUrl ?: ""
                                            accLogoEmoji = goal.emoji ?: ""
                                        }
                                        goalSuggestionsExpanded = false
                                    }
                                )
                            }
                        }
                    }

                    AnimatedVisibility(visible = nameTFV.text.isBlank()) {
                        Column(modifier = Modifier.padding(top = 16.dp)) {
                            Text("Idee:", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                            Spacer(Modifier.height(8.dp))
                            LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                items(defaultGoalSuggestions.take(8)) { goal ->
                                    Surface(
                                        onClick = {
                                            nameTFV = TextFieldValue(goal.name, TextRange(goal.name.length))
                                            if (accLogoUri.isBlank() && accLogoEmoji.isBlank()) {
                                                accLogoUri = goal.customUrl ?: ""
                                                accLogoEmoji = goal.emoji ?: ""
                                            }
                                        },
                                        shape = RoundedCornerShape(16.dp), color = Color.White.copy(alpha = 0.08f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
                                    ) {
                                        Row(modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                                            when {
                                                goal.customUrl != null -> AsyncImage(model = goal.customUrl, contentDescription = null, modifier = Modifier.size(16.dp))
                                                goal.emoji != null -> Text(goal.emoji, fontSize = 14.sp)
                                            }
                                            Spacer(Modifier.width(6.dp))
                                            Text(goal.name, style = MaterialTheme.typography.bodySmall, color = Color.White)
                                        }
                                    }
                                }
                            }
                        }
                    }

                    Spacer(Modifier.height(24.dp))

                    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        OutlinedTextField(
                            value = targetTFV,
                            onValueChange = { targetTFV = it },
                            label = { Text("Obiettivo (€)", color = Color(0xFFA0A0A0)) },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF0284C7), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                        )
                        OutlinedTextField(
                            value = savedTFV,
                            onValueChange = { savedTFV = it },
                            label = { Text("Già Messi (€)", color = Color(0xFFA0A0A0)) },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF0284C7), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                        )
                    }

                    Spacer(Modifier.height(24.dp))

                    // 🚀 INTERRUTTORE CON CONTROLLO SVUOTAMENTO
                    Surface(shape = RoundedCornerShape(24.dp), color = Color.White.copy(alpha = 0.10f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)), modifier = Modifier.fillMaxWidth().height(48.dp)) {
                        Row(modifier = Modifier.fillMaxSize()) {
                            Box(
                                modifier = Modifier.weight(1f).fillMaxHeight().clip(RoundedCornerShape(24.dp))
                                    .clickable { isCompleted = false }.background(if (!isCompleted) Color(0xFF0284C7) else Color.Transparent),
                                contentAlignment = Alignment.Center
                            ) { Text("In Corso", color = if (!isCompleted) Color.White else Color(0xFFA0A0A0), fontWeight = FontWeight.Bold, fontSize = 13.sp) }
                            Box(
                                modifier = Modifier.weight(1f).fillMaxHeight().clip(RoundedCornerShape(24.dp))
                                    .clickable {
                                        val currentSavedAmt = parseEuroToCents(savedTFV.text)
                                        if (currentSavedAmt > 0) {
                                            showNotEmptyAlert = true
                                        } else {
                                            isCompleted = true
                                        }
                                    }.background(if (isCompleted) Color(0xFF0284C7) else Color.Transparent),
                                contentAlignment = Alignment.Center
                            ) { Text("Completato", color = if (isCompleted) Color.White else Color(0xFFA0A0A0), fontWeight = FontWeight.Bold, fontSize = 13.sp) }
                        }
                    }

                    if (showNotEmptyAlert) {
                        AlertDialog(
                            onDismissRequest = { showNotEmptyAlert = false },
                            containerColor = Color(0xFF1E1B4B),
                            title = { Text("Svuota prima il ${if (isPac) "PAC" else "Sogno"}", fontWeight = FontWeight.Black, color = Color.White) },
                            text = { Text("Non puoi archiviare questo obiettivo perché contiene ancora dei fondi.\n\nTorna alla schermata principale e usa il tasto 'Smobilita' (freccia in alto) per decidere se rimettere i soldi nel Disponibile, prelevarli in Contanti o fare un Giroconto.", color = Color.White.copy(alpha=0.7f)) },
                            confirmButton = { TextButton(onClick = { showNotEmptyAlert = false }) { Text("HO CAPITO", fontWeight = FontWeight.Bold, color = brandPrimary) } }
                        )
                    }

                    Spacer(Modifier.height(100.dp)) // Spazio per il FAB
                }
                    }

                    // FAB SALVA (Floating OVER the Column)
                    FloatingActionButton(
                        onClick = {
                            var safeUri = accLogoUri.takeIf { it.isNotBlank() }
                            var safeEmoji = accLogoEmoji.takeIf { it.isNotBlank() }

                            if (safeUri == null && safeEmoji == null) {
                                val match = detectSmartGoal(nameTFV.text)
                                if (match != null) {
                                    safeUri = match.customUrl
                                    safeEmoji = match.emoji
                                }
                            }

                            val finalName = "${nameTFV.text.trim()} $pacTag $accTag".trim()

                            onSave(SavingsGoalEntity(
                                id = initial?.id ?: "sav_${nowMs}",
                                name = finalName,
                                targetAmountCents = parseEuroToCents(targetTFV.text),
                                savedAmountCents = parseEuroToCents(savedTFV.text),
                                emoji = safeEmoji,
                                imageUri = safeUri,
                                isCompleted = isCompleted,
                                isDeleted = false,
                                createdAtEpochMs = initial?.createdAtEpochMs ?: nowMs,
                                updatedAtEpochMs = nowMs
                            ))
                        },
                        modifier = Modifier
                            .align(Alignment.BottomEnd)
                            .navigationBarsPadding()
                            .padding(end = 24.dp, bottom = 64.dp),
                        shape = CircleShape,
                        containerColor = if (canSave) Color(0xFF0284C7) else Color.White.copy(alpha = 0.12f),
                        contentColor = if (canSave) Color.White else Color(0xFFA0A0A0)
                    ) {
                        Icon(Icons.Default.ArrowDownward, contentDescription = "Salva", modifier = Modifier.size(24.dp))
                    }
                }
            }
        }
    }

    if (showWebSearch) {
        WebImagePickerPopup(
            initialQuery = nameTFV.text,
            onDismiss = { showWebSearch = false },
            onImageSelected = { url ->
                accLogoUri = url
                accLogoEmoji = ""
                showWebSearch = false
            }
        )
    }
}